import * as React from "react";
import s from "./ranking.scss";
import { MultiPlaceRetrospectiveData, RetrospectiveData } from "../../api/types";
import { AnimatedRankingItem } from "./animated-ranking-item";
import { rankingContent } from "./content";
import { PlaceCarousel } from "../place-carousel";
import { formatCompactNumber } from "../../utils/format-number";
import { Tooltip } from "#components/primitive";

interface RankingItemProps {
	item: {
		position: number;
		name: string;
		revenue: number;
		quantity: number;
		unit: string;
		percentage: number;
		funFact?: string;
	};
	color: string;
	category?: "drinks" | "food";
}

const DRINK_COLORS = ["yellow", "orange", "red", "purple", "blue"];
const FOOD_COLORS = ["amber", "rose", "green", "cyan", "indigo"];

function capitalizeFirstLetter(text: string): string {
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function RankingItem({
	item,
	color,
	category,
	delay,
}: RankingItemProps & { delay?: number }) {
	const tooltipMessage =
		category === "drinks"
			? "Porcentagem em relação ao faturamento de Bebidas"
			: category === "food"
			? "Porcentagem em relação ao faturamento de Comidas"
			: undefined;

	return (
		<AnimatedRankingItem
			className={`${s.rankingItem} ${s[`ranking${color}`]}`}
			delay={delay}
		>
			<div className={s.rankingItemHeader}>
				<div className={s.rankingItemInfo}>
					<span className={s.rankingPosition}>{item.position}º</span>
					<div className={s.rankingContent}>
						<h4>{capitalizeFirstLetter(item.name)}</h4>
						<p className={s.rankingRevenue}>+R$ {formatCompactNumber(item.revenue)}</p>
						<p className={s.rankingQuantity}>
							+{formatCompactNumber(item.quantity)} {item.unit}
						</p>
					</div>
				</div>
				{item.percentage > 0 &&
					(tooltipMessage ? (
						<Tooltip content={tooltipMessage} placement="top">
							<span className={s.rankingPercentage}>{item.percentage}%</span>
						</Tooltip>
					) : (
						<span className={s.rankingPercentage}>{item.percentage}%</span>
					))}
			</div>
			{item.funFact && <p className={s.rankingNote}>{item.funFact}</p>}
		</AnimatedRankingItem>
	);
}

interface RankingProps {
	ranking: RetrospectiveData["ranking"];
	multiPlaceData?: MultiPlaceRetrospectiveData;
	selectedPlaceIndex?: number;
	onPlaceIndexChange?: (index: number) => void;
	showOrganizationRanking?: boolean;
}

export function Ranking({
	ranking,
	multiPlaceData,
	selectedPlaceIndex = 0,
	onPlaceIndexChange,
	showOrganizationRanking = true,
}: RankingProps) {
	const filteredPlaces = React.useMemo(() => {
		if (!multiPlaceData) return [];
		if (showOrganizationRanking) return multiPlaceData.places;
		return multiPlaceData.places.filter(place => place.id !== "aggregated");
	}, [multiPlaceData, showOrganizationRanking]);

	const adjustedPlaceIndex = React.useMemo(() => {
		if (!multiPlaceData || showOrganizationRanking) return selectedPlaceIndex;

		// Se não mostrar "Todos os lugares", ajustar o índice
		// O índice 0 no places original é "Todos os lugares" (aggregated)
		// No filteredPlaces, o índice 0 é o primeiro place real (que era índice 1 no original)
		if (selectedPlaceIndex === 0) {
			// Se estava no índice 0 (Todos os lugares), ir para o primeiro place real
			// No filteredPlaces, isso é índice 0
			return 0;
		}
		// Caso contrário, reduzir em 1 porque removemos o primeiro item (aggregated)
		return selectedPlaceIndex - 1;
	}, [multiPlaceData, selectedPlaceIndex, showOrganizationRanking]);

	const currentRanking = React.useMemo(() => {
		if (!multiPlaceData) return ranking;

		const places = showOrganizationRanking ? multiPlaceData.places : filteredPlaces;

		const indexToUse = showOrganizationRanking ? selectedPlaceIndex : adjustedPlaceIndex;

		// Verificação de segurança: índice válido
		if (indexToUse < 0 || indexToUse >= places.length) {
			return ranking;
		}

		const currentPlace = places[indexToUse];

		// Verificação de segurança: place existe
		if (!currentPlace) {
			return ranking;
		}

		if (currentPlace.id === "aggregated") {
			return multiPlaceData.aggregated.ranking;
		}

		const placeRanking = multiPlaceData.byPlace[currentPlace.id]?.ranking;

		return placeRanking || ranking;
	}, [
		multiPlaceData,
		selectedPlaceIndex,
		ranking,
		showOrganizationRanking,
		filteredPlaces,
		adjustedPlaceIndex,
	]);

	const hasDrinks = currentRanking.drinks.length > 0;
	const hasFood = currentRanking.food.length > 0;
	const hasOnlyOne = (hasDrinks && !hasFood) || (!hasDrinks && hasFood);

	if (!hasDrinks && !hasFood) {
		return null;
	}

	return (
		<section id="ranking" className={s.ranking}>
			<div className={s.sectionContainer}>
				<h2 className={s.sectionTitle}>{rankingContent.title}</h2>

				{multiPlaceData && onPlaceIndexChange && filteredPlaces.length > 1 && (
					<PlaceCarousel
						places={filteredPlaces}
						currentIndex={adjustedPlaceIndex}
						onChangeIndex={newIndex => {
							// Ajustar o índice de volta para o sistema original
							if (!showOrganizationRanking) {
								// No filteredPlaces, índice 0 = primeiro place real
								// No places original, isso corresponde ao índice 1 (porque índice 0 é aggregated)
								onPlaceIndexChange(newIndex + 1);
							} else {
								onPlaceIndexChange(newIndex);
							}
						}}
					/>
				)}

				<div className={`${s.rankingGrid} ${hasOnlyOne ? s.rankingGridSingle : ""}`}>
					{hasDrinks && (
						<div className={s.rankingColumn}>
							<h3 className={s.rankingTitle}>{rankingContent.drinksTitle}</h3>
							<div className={s.rankingList}>
								{currentRanking.drinks.map((item, index) => (
									<RankingItem
										key={`${item.position}-${item.name}-${index}`}
										item={item}
										color={DRINK_COLORS[index] || "blue"}
										category="drinks"
										delay={index * 100}
									/>
								))}
							</div>
						</div>
					)}

					{hasFood && (
						<div className={s.rankingColumn}>
							<h3 className={`${s.rankingTitle} ${s.rankingTitlePink}`}>
								{rankingContent.foodTitle}
							</h3>
							<div className={s.rankingList}>
								{currentRanking.food.map((item, index) => (
									<RankingItem
										key={`${item.position}-${item.name}-${index}`}
										item={item}
										color={FOOD_COLORS[index] || "indigo"}
										category="food"
										delay={index * 100}
									/>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
